import { ADD_EMPLOYEE_STEPS, type AddEmployeeStep } from '@/types/add-employee'
import { BULK_UPLOAD_ERROR_CODES, type BulkUploadErrorCode } from '@/types/csv'
import {
  COVERAGE_STATUSES,
  type CoverageStatus,
} from '@/lib/utils/employee-filters'
import { GROUP_ID_PATTERN } from '@/lib/validation'
import { logger } from '@/lib/services/logger.service'

import { rewriteHeapEvent } from './rewrite'

/**
 * Every Usage Event and the only properties it may carry. Heap's rewrite hook
 * leaves custom properties alone, so this map and ALLOWED_KEYS are the privacy
 * boundary: ids, counts and fixed categories only, never typed text.
 */
export interface UsageEventProperties {
  pageViewed: { path: string }
  rosterSearched: { employerGroupUuid: string }
  rosterFilterChanged: {
    employerGroupUuid: string
    coverage: CoverageStatus | 'all'
  }
  employeeAddStepViewed: {
    employerGroupUuid: string
    stepName: AddEmployeeStep
    stepIndex: number
  }
  employeeAddSubmitted: { employerGroupUuid: string; dependentCount: number }
  employeeAddSucceeded: { employerGroupUuid: string }
  employeeAddFailed: {
    employerGroupUuid: string
    errorCode: (typeof ADD_FAILED_ERROR_CODES)[number]
  }
  // A Server Action call rejected at the transport level (the proxy's
  // session-expired redirect is one cause); an app error is a result.
  serverActionRejected: { surface: (typeof SERVER_ACTION_SURFACES)[number] }
  // The upload modal's company is optional, so the id is too.
  censusUploadOpened: { employerGroupUuid?: string }
  censusUploadSubmitted: { employerGroupUuid?: string }
  censusUploadSucceeded: { employerGroupUuid?: string }
  censusUploadFailed: {
    employerGroupUuid?: string
    errorCode: BulkUploadErrorCode
  }
}

export type AnalyticsEvent = keyof UsageEventProperties

type EventProperties = Record<string, string>

type HeapStub = unknown[][] &
  Record<string, (...args: unknown[]) => void> & {
    appid?: string
    config?: Record<string, unknown>
  }

declare global {
  interface Window {
    heap?: HeapStub
  }
}

const HEAP_CDN = 'https://cdn.heapanalytics.com/js/heap-'

const QUEUED_METHODS = [
  'addEventProperties',
  'identify',
  'resetIdentity',
  'track',
]

let pendingLoad: Promise<void> = Promise.resolve()

/**
 * Calls queue until the script arrives; the promise settles on its load or
 * error.
 */
export function loadHeap(appId: string): Promise<void> {
  if (window.heap) {
    // Heap reads the hook at event time, so a stub made elsewhere is fixed here.
    window.heap.config = { ...window.heap.config, rewrite: rewriteHeapEvent }
    return pendingLoad
  }

  const stub = Object.assign([], {
    appid: appId,
    config: { rewrite: rewriteHeapEvent },
  }) as unknown as HeapStub
  for (const method of QUEUED_METHODS) {
    stub[method] = (...args: unknown[]) => {
      stub.push([method, ...args])
    }
  }
  window.heap = stub

  const script = document.createElement('script')
  script.async = true
  script.src = `${HEAP_CDN}${appId}.js`

  pendingLoad = new Promise<void>((resolve, reject) => {
    const fail = (reason: unknown) => {
      // A dead queue would swallow every later call and block a retry.
      if (window.heap === stub) delete window.heap
      script.remove()
      reject(reason)
    }
    script.onload = () => resolve()
    script.onerror = () => fail(new Error('Heap script failed to load'))
    try {
      document.head.appendChild(script)
    } catch (error) {
      fail(error)
    }
  })
  return pendingLoad
}

function callHeap(method: string, ...args: unknown[]): void {
  try {
    window.heap?.[method]?.(...args)
  } catch {
    // Analytics must never break the page it measures.
  }
}

export function identify(userId: string): void {
  callHeap('identify', userId)
}

export function resetIdentity(): void {
  callHeap('resetIdentity')
}

export function addEventProperties(properties: EventProperties): void {
  callHeap('addEventProperties', properties)
}

// Types are erased at runtime, so only these keys per event are ever sent.
const ALLOWED_KEYS: {
  [E in AnalyticsEvent]: readonly (keyof UsageEventProperties[E])[]
} = {
  pageViewed: ['path'],
  rosterSearched: ['employerGroupUuid'],
  rosterFilterChanged: ['employerGroupUuid', 'coverage'],
  employeeAddStepViewed: ['employerGroupUuid', 'stepName', 'stepIndex'],
  employeeAddSubmitted: ['employerGroupUuid', 'dependentCount'],
  employeeAddSucceeded: ['employerGroupUuid'],
  employeeAddFailed: ['employerGroupUuid', 'errorCode'],
  serverActionRejected: ['surface'],
  censusUploadOpened: ['employerGroupUuid'],
  censusUploadSubmitted: ['employerGroupUuid'],
  censusUploadSucceeded: ['employerGroupUuid'],
  censusUploadFailed: ['employerGroupUuid', 'errorCode'],
}

const ADD_FAILED_ERROR_CODES = ['enroll_failed', 'enroll_threw'] as const

const SERVER_ACTION_SURFACES = [
  'add_employee_plans',
  'add_employee_submit',
] as const

const isCount = (value: unknown) =>
  Number.isInteger(value) && (value as number) >= 0

// Values that must come from a fixed vocabulary or id shape. Types are erased
// at runtime, so a widened type or a server-supplied string could otherwise
// reach Heap. Free text never matches, so the whole event is dropped.
const VALUE_CHECKS: Record<string, (value: unknown) => boolean> = {
  employerGroupUuid: value =>
    typeof value === 'string' && GROUP_ID_PATTERN.test(value),
  coverage: value =>
    value === 'all' ||
    (COVERAGE_STATUSES as readonly unknown[]).includes(value),
  stepName: value => (ADD_EMPLOYEE_STEPS as readonly unknown[]).includes(value),
  errorCode: value =>
    (BULK_UPLOAD_ERROR_CODES as readonly unknown[]).includes(value) ||
    (ADD_FAILED_ERROR_CODES as readonly unknown[]).includes(value),
  surface: value =>
    (SERVER_ACTION_SURFACES as readonly unknown[]).includes(value),
  dependentCount: isCount,
  stepIndex: isCount,
}

// Returns the first invalid field's name only, never its value.
function findInvalidField(
  keys: readonly string[],
  properties: Record<string, string | number | undefined>,
): string | undefined {
  return keys.find(
    key =>
      properties[key] !== undefined &&
      VALUE_CHECKS[key] !== undefined &&
      !VALUE_CHECKS[key](properties[key]),
  )
}

// Heap takes string values; counts are converted here so callers stay typed.
function toHeapProperties(
  keys: readonly string[],
  properties: Record<string, string | number | undefined> = {},
): Record<string, string> {
  return Object.fromEntries(
    keys
      .filter(key => properties[key] !== undefined)
      .map(key => [key, String(properties[key])]),
  )
}

// Rejects keys the event does not declare, even on a variable, where
// TypeScript's own excess-property check does not run.
type Declared<E extends AnalyticsEvent, P> = P &
  Record<Exclude<keyof P, keyof UsageEventProperties[E]>, never>

export function track<
  E extends AnalyticsEvent,
  P extends UsageEventProperties[E],
>(name: E, properties: Declared<E, P>): void {
  // Erased types let an unknown name through; with no allowlist it is dropped.
  const keys = ALLOWED_KEYS[name] as readonly string[] | undefined
  if (!keys) return
  const values = properties as Record<string, string | number | undefined>
  const invalidField = findInvalidField(keys, values)
  if (invalidField) {
    logger.warn('Usage event dropped: invalid property', {
      event: name,
      field: invalidField,
    })
    return
  }
  callHeap('track', name, toHeapProperties(keys, values))
}
